import { AfterViewInit, Component, ElementRef, OnDestroy, inject } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [RouterLink, DecimalPipe, MatIconModule],
  template: `
    <div class="landing">
      <!-- Navbar -->
      <header class="nav">
        <div class="nav-inner">
          <a class="brand" href="#top" (click)="closeMenu()">
            <img src="/logo.png" alt="" class="brand-logo" />
            <span>SecAudit</span>
          </a>

          <nav class="nav-links" id="nav-menu" [class.open]="menuOpen" aria-label="Principal">
            <a href="#beneficios" (click)="closeMenu()">Beneficios</a>
            <a href="#como-funciona" (click)="closeMenu()">Cómo funciona</a>
            <a href="#cotizador" (click)="closeMenu()">Cotización</a>
            <div class="nav-mobile-actions">
              <a routerLink="/login" class="btn-ghost" (click)="closeMenu()">Iniciar sesión</a>
              <a routerLink="/login" class="btn-primary" (click)="closeMenu()">Solicitar demo</a>
            </div>
          </nav>

          <div class="nav-actions">
            <a routerLink="/login" class="btn-ghost">Iniciar sesión</a>
            <a routerLink="/login" class="btn-primary">Solicitar demo</a>
          </div>

          <button
            type="button"
            class="nav-burger"
            (click)="toggleMenu()"
            [attr.aria-expanded]="menuOpen"
            aria-controls="nav-menu"
            [attr.aria-label]="menuOpen ? 'Cerrar menú' : 'Abrir menú'">
            <span></span><span></span><span></span>
          </button>
        </div>
      </header>

      <!-- Hero -->
      <section class="hero" id="top">
        <div class="hero-bg" aria-hidden="true"></div>
        <div class="hero-inner">
          <span class="badge"><span class="badge-dot"></span>Auditoría de seguridad web</span>
          <h1 data-reveal>
            Protege tu presencia digital con <span class="grad">auditorías inteligentes</span>
          </h1>
          <p class="hero-sub" data-reveal>
            SecAudit analiza tus sitios web, detecta vulnerabilidades y genera reportes
            de seguridad claros y accionables, para que tu equipo actúe antes de que sea tarde.
          </p>
          <div class="hero-actions" data-reveal>
            <a routerLink="/login" class="btn-primary btn-lg">Comenzar ahora</a>
            <a href="#beneficios" class="btn-outline btn-lg">Conocer más</a>
          </div>
          <div class="hero-stats" data-reveal>
            <div class="hero-stat"><strong>24/7</strong><span>Monitoreo continuo</span></div>
            <div class="hero-stat"><strong>IA</strong><span>Detección de riesgos</span></div>
            <div class="hero-stat"><strong>PDF</strong><span>Reportes listos</span></div>
            <div class="hero-stat"><strong>1 clic</strong><span>Auditorías</span></div>
          </div>
        </div>

        <!-- Mockup del producto -->
        <div class="showcase" data-reveal>
          <div class="browser">
            <div class="browser-bar">
              <span class="browser-dot"></span>
              <span class="browser-dot"></span>
              <span class="browser-dot"></span>
              <span class="browser-url">app.secaudit.mx/dashboard</span>
            </div>
            <img
              src="/screenshots/02-dashboard.png"
              alt="Panel de control de SecAudit con el estado de seguridad de tus páginas"
              loading="eager"
              width="1440"
              height="900" />
          </div>
          <div class="float-card">
            <div class="float-card-label">
              <mat-icon>bug_report</mat-icon>
              <span>Detalle de vulnerabilidades</span>
            </div>
            <div class="float-card-img">
              <img
                src="/screenshots/11-detalle-vulnerabilidades.png"
                alt="Detalle de vulnerabilidades detectadas con severidad y recomendaciones"
                loading="lazy"
                width="1440"
                height="900" />
            </div>
          </div>
        </div>
      </section>

      <!-- Beneficios -->
      <section id="beneficios" class="section section-features">
        <div class="section-inner">
          <h2 data-reveal>Deja de reaccionar, empieza a prevenir</h2>
          <p class="subtitle" data-reveal>
            Las vulnerabilidades no avisan. Nosotros las encontramos por ti.
          </p>
          <div class="bento">
            <article class="card card-wide" data-reveal>
              <div class="card-icon"><mat-icon>shield</mat-icon></div>
              <h3>Escaneo de vulnerabilidades</h3>
              <p>Detecta configuraciones inseguras, cabeceras faltantes y riesgos comunes en tus sitios.</p>
              <ul class="card-list">
                <li><mat-icon>check_circle</mat-icon> Cabeceras de seguridad</li>
                <li><mat-icon>check_circle</mat-icon> Cookies y formularios</li>
                <li><mat-icon>check_circle</mat-icon> SSL/TLS y configuración</li>
              </ul>
            </article>
            <article class="card" data-reveal>
              <div class="card-icon"><mat-icon>description</mat-icon></div>
              <h3>Reportes profesionales</h3>
              <p>Genera reportes de seguridad en PDF con hallazgos, severidad y recomendaciones.</p>
            </article>
            <article class="card" data-reveal>
              <div class="card-icon"><mat-icon>notifications_active</mat-icon></div>
              <h3>Alertas de cambios</h3>
              <p>Recibe notificaciones cuando algo cambia en la seguridad de tus páginas monitoreadas.</p>
            </article>
            <article class="card" data-reveal>
              <div class="card-icon"><mat-icon>dashboard</mat-icon></div>
              <h3>Panel de control</h3>
              <p>Visualiza el estado de seguridad de todas tus páginas en un solo lugar.</p>
            </article>
            <article class="card" data-reveal>
              <div class="card-icon"><mat-icon>admin_panel_settings</mat-icon></div>
              <h3>Roles y permisos</h3>
              <p>Controla quién puede ver y gestionar cada auditoría con roles personalizados.</p>
            </article>
            <article class="card" data-reveal>
              <div class="card-icon"><mat-icon>verified</mat-icon></div>
              <h3>Cumplimiento</h3>
              <p>Mantén evidencia documentada de tus auditorías para auditorías internas y externas.</p>
            </article>
          </div>
        </div>
      </section>

      <!-- Cómo funciona -->
      <section id="como-funciona" class="section section-alt">
        <div class="section-inner">
          <h2 data-reveal>Cómo funciona</h2>
          <p class="subtitle" data-reveal>Tres pasos para asegurar tus sitios.</p>
          <div class="steps">
            <div class="steps-line" aria-hidden="true"></div>
            <article class="step" data-reveal>
              <div class="step-num">01</div>
              <h3>Registra tus páginas</h3>
              <p>Agrega los sitios web que quieres monitorear.</p>
            </article>
            <article class="step" data-reveal>
              <div class="step-num">02</div>
              <h3>Ejecuta auditorías</h3>
              <p>El sistema analiza y detecta vulnerabilidades automáticamente.</p>
            </article>
            <article class="step" data-reveal>
              <div class="step-num">03</div>
              <h3>Actúa con el reporte</h3>
              <p>Recibe un reporte claro con recomendaciones para corregir.</p>
            </article>
          </div>
        </div>
      </section>

      <!-- Cotizador -->
      <section id="cotizador" class="section section-quote">
        <div class="section-inner">
          <h2 data-reveal>Cotiza tu auditoría en segundos</h2>
          <p class="subtitle" data-reveal>
            Dinos el tamaño de tu empresa y te mostramos el precio de una auditoría
            integral de seguridad web. Sin costos ocultos.
          </p>

          <div class="quote-wrap" data-reveal>
            <!-- Controles -->
            <div class="quote-controls">
              <span class="quote-label" id="quote-size-label">¿Qué tamaño tiene tu empresa?</span>
              <div class="quote-sizes" role="group" aria-labelledby="quote-size-label">
                @for (s of sizes; track s.id) {
                  <button
                    type="button"
                    class="quote-size"
                    [class.active]="s.id === selectedSize"
                    [attr.aria-pressed]="s.id === selectedSize"
                    (click)="selectSize(s.id)">
                    <span class="quote-size-icon"><mat-icon>{{ s.icon }}</mat-icon></span>
                    <span class="quote-size-name">{{ s.name }}</span>
                    <span class="quote-size-range">{{ s.range }}</span>
                  </button>
                }
              </div>
              <p class="quote-hint">
                La auditoría cubre tus sitios web principales: análisis de vulnerabilidades,
                configuración, cabeceras de seguridad, cookies, formularios y SSL/TLS.
              </p>
            </div>

            <!-- Resultado -->
            <div class="quote-result">
              <span class="quote-result-label">Inversión estimada</span>
              <div class="quote-price">
                <span class="quote-currency">$</span>
                <span class="quote-amount">{{ selected.price | number: '1.0-0' }}</span>
                <span class="quote-unit">pesos</span>
              </div>
              <span class="quote-per">por auditoría integral</span>

              <ul class="quote-includes">
                <li><mat-icon>check_circle</mat-icon> Análisis completo de vulnerabilidades</li>
                <li><mat-icon>check_circle</mat-icon> Reporte ejecutivo y técnico en PDF</li>
                <li><mat-icon>check_circle</mat-icon> Recomendaciones priorizadas por severidad</li>
                <li><mat-icon>check_circle</mat-icon> Entrega en 5 días hábiles</li>
                <li><mat-icon>check_circle</mat-icon> Sesión de resultados con tu equipo</li>
              </ul>

              <a routerLink="/login" class="quote-cta">Solicitar esta auditoría</a>
              <span class="quote-note">Precio en pesos mexicanos (MXN) · IVA no incluido</span>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA -->
      <section class="section section-cta">
        <div class="section-inner">
          <div class="cta-panel" data-reveal>
            <h2>¿Listo para asegurar tus sitios?</h2>
            <p>Únete a las organizaciones que ya confían en SecAudit para su seguridad web.</p>
            <a routerLink="/login" class="btn-primary btn-lg">Solicitar demo</a>
            <div class="cta-tags">
              <span><mat-icon>check_circle</mat-icon> Sin costo inicial</span>
              <span><mat-icon>check_circle</mat-icon> Configuración en minutos</span>
              <span><mat-icon>check_circle</mat-icon> Soporte en español</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Footer -->
      <footer class="footer">
        <div class="footer-inner">
          <div class="footer-top">
            <a class="brand" href="#top">
              <img src="/logo.png" alt="" class="brand-logo" />
              <span>SecAudit</span>
            </a>
            <nav class="footer-links" aria-label="Pie de página">
              <a href="#beneficios">Beneficios</a>
              <a href="#como-funciona">Cómo funciona</a>
              <a href="#cotizador">Cotización</a>
              <a routerLink="/login">Iniciar sesión</a>
            </nav>
          </div>
          <p>© {{ year }} SecAudit · Sistema de Auditoría de Seguridad</p>
        </div>
      </footer>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        font-family: var(--font-body);
        color: var(--text-primary);
        background: var(--surface-color);
      }

      a:focus-visible,
      button:focus-visible {
        outline: 2px solid var(--primary-light);
        outline-offset: 3px;
        border-radius: 4px;
      }

      .landing {
        min-height: 100vh;
        overflow-x: clip;
      }

      h1,
      h2,
      h3 {
        font-family: var(--font-display);
        letter-spacing: -0.02em;
      }

      /* ---------- Botones ---------- */
      .btn-primary,
      .btn-ghost,
      .btn-outline {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        font-family: var(--font-body);
        font-size: 14px;
        font-weight: 600;
        text-decoration: none;
        transition:
          background 0.2s,
          border-color 0.2s,
          box-shadow 0.2s,
          transform 0.2s;
        cursor: pointer;
      }
      .btn-primary {
        background: var(--primary-color);
        color: #fff;
        padding: 10px 20px;
        box-shadow: 0 1px 2px rgba(183, 28, 28, 0.3);
      }
      .btn-primary:hover {
        background: var(--primary-dark);
        box-shadow: 0 6px 20px rgba(183, 28, 28, 0.35);
        transform: translateY(-1px);
      }
      .btn-ghost {
        color: var(--text-secondary);
        padding: 10px 16px;
      }
      .btn-ghost:hover {
        background: rgba(0, 0, 0, 0.05);
        color: var(--text-primary);
      }
      .btn-outline {
        border: 1px solid rgba(255, 255, 255, 0.18);
        background: rgba(255, 255, 255, 0.04);
        color: var(--text-on-dark);
        padding: 10px 20px;
        backdrop-filter: blur(6px);
      }
      .btn-outline:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.32);
      }
      .btn-lg {
        padding: 15px 30px;
        font-size: 15px;
        border-radius: 12px;
      }

      /* ---------- Navbar ---------- */
      .nav {
        position: sticky;
        top: 0;
        z-index: 50;
        background: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(14px) saturate(160%);
        -webkit-backdrop-filter: blur(14px) saturate(160%);
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
      }
      .nav-inner {
        max-width: 1180px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 14px 24px;
      }
      .brand {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        font-family: var(--font-display);
        font-weight: 700;
        font-size: 18px;
        color: inherit;
        text-decoration: none;
        letter-spacing: -0.02em;
      }
      .brand-logo {
        width: 34px;
        height: 34px;
        border-radius: 9px;
        object-fit: cover;
        box-shadow: var(--shadow-sm);
      }
      .nav-links {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        font-weight: 500;
      }
      .nav-links > a {
        color: var(--text-secondary);
        text-decoration: none;
        padding: 8px 14px;
        border-radius: 999px;
        transition:
          color 0.2s,
          background 0.2s;
      }
      .nav-links > a:hover {
        color: var(--text-primary);
        background: rgba(0, 0, 0, 0.05);
      }
      .nav-actions {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .nav-mobile-actions {
        display: none;
      }
      .nav-burger {
        display: none;
        flex-direction: column;
        justify-content: center;
        gap: 5px;
        width: 42px;
        height: 42px;
        padding: 10px;
        border: 1px solid rgba(0, 0, 0, 0.08);
        border-radius: 10px;
        background: #fff;
        cursor: pointer;
      }
      .nav-burger span {
        display: block;
        height: 2px;
        width: 100%;
        background: var(--text-primary);
        border-radius: 2px;
        transition: transform 0.2s, opacity 0.2s;
      }

      /* ---------- Hero ---------- */
      .hero {
        position: relative;
        background: var(--surface-dark);
        padding: 96px 24px 0;
        overflow: visible;
      }
      .hero-bg {
        position: absolute;
        inset: 0;
        pointer-events: none;
        background-image:
          linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px),
          radial-gradient(ellipse 70% 55% at 50% -5%, rgba(183, 28, 28, 0.35), transparent 65%),
          radial-gradient(ellipse 45% 40% at 85% 30%, rgba(183, 28, 28, 0.12), transparent 70%);
        background-size: 56px 56px, 56px 56px, 100% 100%, 100% 100%;
        mask-image: radial-gradient(ellipse 90% 80% at 50% 20%, #000 30%, transparent 85%);
        -webkit-mask-image: radial-gradient(ellipse 90% 80% at 50% 20%, #000 30%, transparent 85%);
      }
      .hero-inner {
        position: relative;
        z-index: 1;
        max-width: 860px;
        margin: 0 auto;
        text-align: center;
      }
      .badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px 14px 6px 10px;
        border-radius: 999px;
        border: 1px solid rgba(254, 202, 202, 0.22);
        background: rgba(183, 28, 28, 0.12);
        color: var(--text-soft-on-dark);
        font-size: 13px;
        font-weight: 500;
      }
      .badge-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--primary-light);
        box-shadow: 0 0 0 4px rgba(240, 85, 69, 0.18);
      }
      .hero h1 {
        margin: 22px auto 0;
        font-size: clamp(34px, 5.4vw, 56px);
        line-height: 1.08;
        font-weight: 800;
        color: var(--text-on-dark);
      }
      .grad {
        background: linear-gradient(95deg, #f05545 0%, #ff8a8a 50%, #ffc2c2 100%);
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
      }
      .hero-sub {
        max-width: 620px;
        margin: 22px auto 0;
        font-size: 18px;
        line-height: 1.65;
        color: var(--text-muted-on-dark);
      }
      .hero-actions {
        display: flex;
        justify-content: center;
        gap: 14px;
        margin-top: 36px;
        flex-wrap: wrap;
      }
      .hero-stats {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 0;
        margin-top: 48px;
        border-top: 1px solid var(--border-dark);
        border-bottom: 1px solid var(--border-dark);
      }
      .hero-stat {
        flex: 1 1 140px;
        padding: 18px 16px;
        position: relative;
      }
      .hero-stat + .hero-stat::before {
        content: '';
        position: absolute;
        left: 0;
        top: 22%;
        height: 56%;
        width: 1px;
        background: var(--border-dark);
      }
      .hero-stat strong {
        display: block;
        font-family: var(--font-display);
        font-size: 22px;
        font-weight: 700;
        color: var(--text-on-dark);
        letter-spacing: -0.02em;
      }
      .hero-stat span {
        display: block;
        margin-top: 4px;
        font-size: 13px;
        color: var(--text-muted-on-dark);
      }

      /* ---------- Showcase / mockup ---------- */
      .showcase {
        position: relative;
        z-index: 2;
        max-width: 1020px;
        margin: 56px auto -110px;
        padding-bottom: 24px;
      }
      .showcase::before {
        content: '';
        position: absolute;
        left: 8%;
        right: 8%;
        bottom: -12px;
        height: 90px;
        background: radial-gradient(ellipse, rgba(183, 28, 28, 0.4), transparent 70%);
        filter: blur(18px);
        pointer-events: none;
      }
      .browser {
        position: relative;
        border-radius: 14px;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.1);
        background: var(--surface-dark-2);
        box-shadow:
          var(--shadow-dark),
          0 0 0 1px rgba(255, 255, 255, 0.03) inset;
      }
      .browser-bar {
        display: flex;
        align-items: center;
        gap: 7px;
        padding: 11px 14px;
        background: var(--surface-dark-3);
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      }
      .browser-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.12);
      }
      .browser-dot:nth-child(1) {
        background: #ff5f57;
        opacity: 0.85;
      }
      .browser-dot:nth-child(2) {
        background: #febc2e;
        opacity: 0.85;
      }
      .browser-dot:nth-child(3) {
        background: #28c840;
        opacity: 0.85;
      }
      .browser-url {
        margin-left: 10px;
        flex: 0 1 320px;
        padding: 5px 14px;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.06);
        color: var(--text-muted-on-dark);
        font-size: 12px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .browser img {
        display: block;
        width: 100%;
        height: auto;
      }
      .float-card {
        position: absolute;
        right: -3%;
        bottom: -6%;
        width: min(42%, 420px);
        border-radius: 12px;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.12);
        background: var(--surface-dark-2);
        box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
      }
      .float-card-label {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 14px;
        background: var(--surface-dark-3);
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        color: var(--text-soft-on-dark);
        font-size: 12px;
        font-weight: 600;
      }
      .float-card-label mat-icon {
        width: 16px;
        height: 16px;
        font-size: 16px;
      }
      .float-card-img {
        max-height: 210px;
        overflow: hidden;
      }
      .float-card-img img {
        display: block;
        width: 100%;
        height: auto;
      }

      /* ---------- Secciones ---------- */
      .section {
        padding: 96px 24px;
      }
      .section-features {
        padding-top: 180px;
      }
      .section-alt {
        background: #f7f8fa;
      }
      .section-quote {
        background: linear-gradient(180deg, #ffffff 0%, #fdf4f4 100%);
      }
      .section-inner {
        max-width: 1180px;
        margin: 0 auto;
        text-align: center;
      }
      .section h2 {
        font-size: clamp(28px, 3.6vw, 38px);
        font-weight: 800;
        color: var(--text-primary);
      }
      .subtitle {
        max-width: 560px;
        margin: 14px auto 0;
        font-size: 17px;
        line-height: 1.6;
        color: var(--text-secondary);
      }

      /* ---------- Bento ---------- */
      .bento {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 18px;
        margin-top: 52px;
        text-align: left;
      }
      .card {
        position: relative;
        background: var(--surface-color);
        border: 1px solid rgba(0, 0, 0, 0.07);
        border-radius: var(--radius-lg);
        padding: 26px;
        box-shadow: var(--shadow-sm);
        transition:
          transform 0.25s ease,
          box-shadow 0.25s ease,
          border-color 0.25s ease;
      }
      .card:hover {
        transform: translateY(-3px);
        border-color: rgba(183, 28, 28, 0.28);
        box-shadow: var(--shadow-md);
      }
      .card-wide {
        grid-row: span 2;
        display: flex;
        flex-direction: column;
        background:
          radial-gradient(ellipse 90% 60% at 100% 0%, rgba(183, 28, 28, 0.06), transparent 60%),
          var(--surface-color);
      }
      .card-icon {
        width: 44px;
        height: 44px;
        border-radius: var(--radius-md);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: rgba(183, 28, 28, 0.09);
        color: var(--primary-color);
      }
      .card-icon mat-icon {
        width: 24px;
        height: 24px;
        font-size: 24px;
      }
      .card h3 {
        margin-top: 16px;
        font-size: 17px;
        font-weight: 700;
      }
      .card p {
        margin-top: 8px;
        font-size: 14px;
        line-height: 1.65;
        color: var(--text-secondary);
      }
      .card-list {
        list-style: none;
        padding: 0;
        margin: auto 0 0;
        padding-top: 20px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        border-top: 1px solid rgba(0, 0, 0, 0.06);
      }
      .card-list li {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 13.5px;
        font-weight: 500;
        color: var(--text-primary);
      }
      .card-list mat-icon {
        width: 18px;
        height: 18px;
        font-size: 18px;
        color: var(--primary-light);
      }

      /* ---------- Pasos ---------- */
      .steps {
        position: relative;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 28px;
        margin-top: 56px;
        text-align: left;
      }
      .steps-line {
        position: absolute;
        top: 26px;
        left: 12%;
        right: 12%;
        height: 2px;
        background: linear-gradient(90deg, transparent, rgba(183, 28, 28, 0.25) 15%, rgba(183, 28, 28, 0.25) 85%, transparent);
      }
      .step {
        position: relative;
        background: var(--surface-color);
        border: 1px solid rgba(0, 0, 0, 0.06);
        border-radius: var(--radius-lg);
        padding: 28px;
        box-shadow: var(--shadow-sm);
        transition:
          transform 0.25s ease,
          box-shadow 0.25s ease;
      }
      .step:hover {
        transform: translateY(-3px);
        box-shadow: var(--shadow-md);
      }
      .step-num {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 46px;
        height: 34px;
        padding: 0 12px;
        border-radius: 999px;
        background: var(--primary-color);
        color: #fff;
        font-family: var(--font-display);
        font-size: 14px;
        font-weight: 700;
        letter-spacing: 0.04em;
        box-shadow: 0 4px 14px rgba(183, 28, 28, 0.28);
      }
      .step h3 {
        margin-top: 18px;
        font-size: 18px;
        font-weight: 700;
      }
      .step p {
        margin-top: 8px;
        font-size: 14px;
        line-height: 1.65;
        color: var(--text-secondary);
      }

      /* ---------- Cotizador ---------- */
      .quote-wrap {
        display: grid;
        grid-template-columns: 1.1fr 1fr;
        gap: 26px;
        max-width: 1020px;
        margin: 52px auto 0;
        text-align: left;
        align-items: stretch;
      }
      .quote-controls {
        background: var(--surface-color);
        border: 1px solid rgba(0, 0, 0, 0.07);
        border-radius: var(--radius-xl);
        padding: 30px;
        box-shadow: var(--shadow-sm);
      }
      .quote-label {
        display: block;
        font-size: 15px;
        font-weight: 700;
        color: var(--text-primary);
        margin-bottom: 16px;
      }
      .quote-sizes {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .quote-size {
        display: grid;
        grid-template-columns: 42px 1fr;
        grid-template-rows: auto auto;
        align-items: center;
        column-gap: 14px;
        width: 100%;
        text-align: left;
        background: var(--surface-color);
        border: 1.5px solid rgba(0, 0, 0, 0.08);
        border-radius: var(--radius-md);
        padding: 12px 16px;
        cursor: pointer;
        font-family: var(--font-body);
        transition:
          border-color 0.15s ease,
          background 0.15s ease,
          box-shadow 0.15s ease;
      }
      .quote-size:hover {
        border-color: rgba(183, 28, 28, 0.35);
        background: #fdf7f7;
      }
      .quote-size.active {
        border-color: var(--primary-color);
        background: #fef2f2;
        box-shadow: 0 0 0 3px rgba(183, 28, 28, 0.1);
      }
      .quote-size-icon {
        grid-row: span 2;
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: rgba(183, 28, 28, 0.08);
        color: var(--primary-color);
      }
      .quote-size.active .quote-size-icon {
        background: rgba(183, 28, 28, 0.14);
      }
      .quote-size-icon mat-icon {
        width: 22px;
        height: 22px;
        font-size: 22px;
      }
      .quote-size-name {
        font-size: 15px;
        font-weight: 600;
        color: var(--text-primary);
      }
      .quote-size-range {
        font-size: 13px;
        color: var(--text-secondary);
      }
      .quote-hint {
        margin-top: 20px;
        font-size: 13px;
        line-height: 1.6;
        color: var(--text-secondary);
      }

      .quote-result {
        position: relative;
        background:
          radial-gradient(ellipse 110% 80% at 100% 0%, rgba(183, 28, 28, 0.45), transparent 55%),
          linear-gradient(165deg, var(--surface-dark-2) 0%, var(--surface-dark) 100%);
        border: 1px solid rgba(255, 255, 255, 0.07);
        border-radius: var(--radius-xl);
        padding: 32px;
        color: var(--text-on-dark);
        display: flex;
        flex-direction: column;
        box-shadow: var(--shadow-dark), var(--glow-red);
        overflow: hidden;
      }
      .quote-result-label {
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 1.2px;
        color: var(--text-soft-on-dark);
        font-weight: 600;
      }
      .quote-price {
        display: flex;
        align-items: baseline;
        gap: 5px;
        margin-top: 10px;
        flex-wrap: wrap;
      }
      .quote-currency {
        font-family: var(--font-display);
        font-size: 26px;
        font-weight: 700;
        color: var(--text-soft-on-dark);
      }
      .quote-amount {
        font-family: var(--font-display);
        font-size: 52px;
        font-weight: 800;
        letter-spacing: -0.03em;
        line-height: 1;
      }
      .quote-unit {
        font-size: 17px;
        font-weight: 600;
        color: var(--text-soft-on-dark);
      }
      .quote-per {
        font-size: 14px;
        color: var(--text-muted-on-dark);
        margin-top: 6px;
      }
      .quote-includes {
        list-style: none;
        padding: 0;
        margin: 26px 0 28px;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .quote-includes li {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        font-size: 14px;
        line-height: 1.45;
        color: #f3d1d1;
      }
      .quote-includes mat-icon {
        width: 18px;
        height: 18px;
        font-size: 18px;
        color: var(--primary-light);
        flex-shrink: 0;
        margin-top: 1px;
      }
      .quote-cta {
        display: block;
        text-align: center;
        background: #fff;
        color: var(--primary-color);
        padding: 15px 24px;
        border-radius: 12px;
        font-size: 15px;
        font-weight: 700;
        text-decoration: none;
        transition:
          background 0.2s,
          transform 0.2s,
          box-shadow 0.2s;
        margin-top: auto;
      }
      .quote-cta:hover {
        background: #ffe9e9;
        transform: translateY(-1px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
      }
      .quote-note {
        display: block;
        text-align: center;
        margin-top: 12px;
        font-size: 12px;
        color: var(--text-muted-on-dark);
      }

      /* ---------- CTA ---------- */
      .section-cta {
        padding-top: 24px;
      }
      .cta-panel {
        position: relative;
        border-radius: var(--radius-xl);
        padding: 72px 28px;
        text-align: center;
        background:
          radial-gradient(ellipse 80% 100% at 50% 130%, rgba(183, 28, 28, 0.45), transparent 60%),
          radial-gradient(ellipse 50% 60% at 85% 0%, rgba(183, 28, 28, 0.18), transparent 65%),
          var(--surface-dark);
        border: 1px solid rgba(255, 255, 255, 0.06);
        overflow: hidden;
        box-shadow: var(--shadow-dark);
      }
      .cta-panel::before {
        content: '';
        position: absolute;
        inset: 0;
        background-image:
          linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
        background-size: 48px 48px;
        mask-image: radial-gradient(ellipse 70% 70% at 50% 50%, #000 20%, transparent 80%);
        -webkit-mask-image: radial-gradient(ellipse 70% 70% at 50% 50%, #000 20%, transparent 80%);
        pointer-events: none;
      }
      .cta-panel h2 {
        position: relative;
        color: var(--text-on-dark);
      }
      .cta-panel p {
        position: relative;
        max-width: 520px;
        margin: 14px auto 0;
        color: var(--text-muted-on-dark);
        font-size: 17px;
        line-height: 1.6;
      }
      .cta-panel .btn-primary {
        position: relative;
        margin-top: 30px;
        background: #fff;
        color: var(--primary-color);
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      }
      .cta-panel .btn-primary:hover {
        background: #ffe9e9;
        box-shadow: 0 14px 36px rgba(0, 0, 0, 0.35);
      }
      .cta-tags {
        position: relative;
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 22px;
        margin-top: 30px;
        color: var(--text-soft-on-dark);
        font-size: 13.5px;
        font-weight: 500;
      }
      .cta-tags span {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .cta-tags mat-icon {
        width: 16px;
        height: 16px;
        font-size: 16px;
        color: var(--primary-light);
      }

      /* ---------- Footer ---------- */
      .footer {
        border-top: 1px solid rgba(0, 0, 0, 0.06);
        padding: 40px 24px;
        background: var(--surface-color);
      }
      .footer-inner {
        max-width: 1180px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        gap: 20px;
      }
      .footer-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 16px;
      }
      .footer-links {
        display: flex;
        flex-wrap: wrap;
        gap: 22px;
        font-size: 14px;
        font-weight: 500;
      }
      .footer-links a {
        color: var(--text-secondary);
        text-decoration: none;
        transition: color 0.2s;
      }
      .footer-links a:hover {
        color: var(--primary-color);
      }
      .footer p {
        color: var(--text-secondary);
        font-size: 13.5px;
      }

      /* ---------- Reveal on scroll ---------- */
      :host.reveal-on [data-reveal] {
        opacity: 0;
        transform: translateY(18px);
        transition:
          opacity 0.65s ease,
          transform 0.65s ease;
      }
      :host.reveal-on [data-reveal].is-visible {
        opacity: 1;
        transform: none;
      }
      :host.reveal-on .bento .card:nth-child(2) {
        transition-delay: 0.05s;
      }
      :host.reveal-on .bento .card:nth-child(3) {
        transition-delay: 0.1s;
      }
      :host.reveal-on .bento .card:nth-child(4) {
        transition-delay: 0.05s;
      }
      :host.reveal-on .bento .card:nth-child(5) {
        transition-delay: 0.1s;
      }
      :host.reveal-on .bento .card:nth-child(6) {
        transition-delay: 0.15s;
      }
      @media (prefers-reduced-motion: reduce) {
        :host.reveal-on [data-reveal] {
          opacity: 1;
          transform: none;
          transition: none;
        }
        .card:hover,
        .step:hover,
        .btn-primary:hover,
        .quote-cta:hover {
          transform: none;
        }
      }

      /* ---------- Responsive ---------- */
      @media (max-width: 980px) {
        .bento {
          grid-template-columns: repeat(2, 1fr);
        }
        .card-wide {
          grid-row: auto;
          grid-column: span 2;
        }
        .float-card {
          width: min(48%, 340px);
          right: 0;
        }
      }

      @media (max-width: 900px) {
        .nav-links {
          display: none;
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          flex-direction: column;
          align-items: stretch;
          gap: 4px;
          padding: 14px 20px 20px;
          background: rgba(255, 255, 255, 0.97);
          backdrop-filter: blur(14px);
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          box-shadow: var(--shadow-md);
        }
        .nav-links.open {
          display: flex;
        }
        .nav-links > a {
          padding: 12px 14px;
          border-radius: 10px;
        }
        .nav-mobile-actions {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 12px;
        }
        .nav-mobile-actions .btn-primary,
        .nav-mobile-actions .btn-ghost {
          width: 100%;
        }
        .nav-mobile-actions .btn-ghost {
          border: 1px solid rgba(0, 0, 0, 0.08);
        }
        .nav-actions {
          display: none;
        }
        .nav-burger {
          display: inline-flex;
        }
        .nav {
          position: sticky;
        }
        .nav-inner {
          position: relative;
        }

        .steps {
          grid-template-columns: 1fr;
        }
        .steps-line {
          display: none;
        }
        .quote-wrap {
          grid-template-columns: 1fr;
        }
        .hero {
          padding-top: 64px;
        }
        .showcase {
          margin-bottom: -70px;
        }
        .float-card {
          position: relative;
          right: auto;
          bottom: auto;
          width: 100%;
          margin-top: 16px;
        }
        .float-card-img {
          max-height: 180px;
        }
        .section-features {
          padding-top: 130px;
        }
      }

      @media (max-width: 640px) {
        .bento {
          grid-template-columns: 1fr;
        }
        .card-wide {
          grid-column: auto;
        }
        .hero h1 {
          font-size: 30px;
        }
        .hero-sub {
          font-size: 16px;
        }
        .hero {
          padding: 48px 16px 0;
        }
        .section {
          padding: 64px 16px;
        }
        .section-features {
          padding-top: 110px;
        }
        .hero-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }
        .hero-stat + .hero-stat::before {
          display: none;
        }
        .hero-stat {
          border-top: 1px solid var(--border-dark);
        }
        .hero-stat:nth-child(-n + 2) {
          border-top: none;
        }
        .quote-controls,
        .quote-result {
          padding: 22px;
        }
        .quote-amount {
          font-size: 42px;
        }
        .cta-panel {
          padding: 52px 20px;
        }
        .btn-lg {
          width: 100%;
        }
        .hero-actions {
          flex-direction: column;
          align-items: stretch;
        }
      }
    `,
  ],
})
export class LandingComponent implements AfterViewInit, OnDestroy {
  private readonly el = inject(ElementRef<HTMLElement>);
  private observer?: IntersectionObserver;

  year = new Date().getFullYear();
  menuOpen = false;
  selectedSize = 'pequena';

  constructor() {
    if (typeof IntersectionObserver !== 'undefined' && !this.reduceMotion()) {
      this.el.nativeElement.classList.add('reveal-on');
    }
  }

  readonly sizes = [
    { id: 'micro', icon: 'home', name: 'Micro', range: '1 a 10 empleados', price: 55500 },
    { id: 'pequena', icon: 'apartment', name: 'Pequeña', range: '11 a 50 empleados', price: 88000 },
    { id: 'mediana', icon: 'domain', name: 'Mediana', range: '51 a 250 empleados', price: 120000 },
    { id: 'grande', icon: 'business', name: 'Grande', range: '251 a 1,000 empleados', price: 152500 },
    {
      id: 'corporativo',
      icon: 'corporate_fare',
      name: 'Corporativo',
      range: 'Más de 1,000 empleados',
      price: 185000,
    },
  ];

  get selected() {
    return this.sizes.find((s) => s.id === this.selectedSize) ?? this.sizes[1];
  }

  selectSize(id: string): void {
    this.selectedSize = id;
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  closeMenu(): void {
    this.menuOpen = false;
  }

  ngAfterViewInit(): void {
    if (typeof IntersectionObserver === 'undefined' || this.reduceMotion()) {
      return;
    }

    const root = this.el.nativeElement;
    const targets = Array.from(root.querySelectorAll('[data-reveal]')) as HTMLElement[];
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('is-visible');
            this.observer?.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    );
    targets.forEach((t) => this.observer?.observe(t));
  }

  private reduceMotion(): boolean {
    return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
